import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

// New logo image URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/mnjq1cak_Recovery%20Room%20Archive-07.png";

const MainNav = () => {
    const location = useLocation();
    const [openDropdown, setOpenDropdown] = useState(null);

    const navItems = [
        {
            label: 'Home',
            href: '/join',
        },
        {
            label: 'Who We Are',
            children: [
                { label: 'Our Story', href: '/who-we-are/story' },
                { label: 'Meet the Founder', href: '/who-we-are/founder' },
                { label: 'Meet the Team', href: '/who-we-are/team' },
                { label: 'What Brothers Are Saying', href: '/who-we-are/testimonials' },
            ]
        },
        {
            label: 'In The Community',
            children: [
                { label: 'Events', href: '/community/events' },
                { label: 'Resources', href: '/community/resources' },
                { label: 'Monthly Newsletter', href: '/community/newsletter' },
                { label: 'Community', href: '/community/connect' },
            ]
        },
    ];

    const handleMouseEnter = (label) => {
        setOpenDropdown(label);
    };

    const handleMouseLeave = () => {
        setOpenDropdown(null);
    };

    return (
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#3D4435]/95 backdrop-blur-lg border-b border-[#5A6350]">
            <div className="max-w-7xl mx-auto px-6 py-4">
                <div className="flex items-center justify-between">
                    {/* Logo */}
                    <Link to="/join" className="flex items-center gap-3">
                        <img 
                            src={LOGO_URL} 
                            alt="Recovery Room Logo" 
                            className="w-10 h-10 object-contain"
                        />
                        <div>
                            <h1 className="text-xl font-bold text-white font-['Playfair_Display']">Recovery Room</h1>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
                        {navItems.map((item) => (
                            <div 
                                key={item.label}
                                className="relative"
                                onMouseEnter={() => item.children && handleMouseEnter(item.label)}
                                onMouseLeave={handleMouseLeave}
                            >
                                {item.children ? (
                                    <>
                                        <button
                                            className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                                openDropdown === item.label 
                                                    ? 'text-[#C9A227] bg-[#C9A227]/10' 
                                                    : 'text-white hover:text-[#C9A227] hover:bg-[#C9A227]/5'
                                            }`}
                                            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                                        >
                                            {item.label}
                                            <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                                        </button>
                                        
                                        {/* Dropdown Menu */}
                                        {openDropdown === item.label && (
                                            <div className="absolute top-full left-0 mt-1 py-2 w-56 bg-[#4A5240] border border-[#5A6350] rounded-xl shadow-2xl">
                                                {item.children.map((child) => (
                                                    <Link
                                                        key={child.href}
                                                        to={child.href}
                                                        className={`block px-4 py-2.5 text-sm transition-colors ${
                                                            location.pathname === child.href
                                                                ? 'text-[#C9A227] bg-[#C9A227]/10'
                                                                : 'text-[#A0A0A0] hover:text-white hover:bg-[#5A6350]'
                                                        }`}
                                                        data-testid={`nav-${child.label.toLowerCase().replace(/\s/g, '-')}`}
                                                    >
                                                        {child.label}
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <Link
                                        to={item.href}
                                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                            location.pathname === item.href
                                                ? 'text-[#C9A227] bg-[#C9A227]/10'
                                                : 'text-white hover:text-[#C9A227] hover:bg-[#C9A227]/5'
                                        }`}
                                        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                                    >
                                        {item.label}
                                    </Link>
                                )}
                            </div>
                        ))}
                    </nav>

                    {/* Right Side - Member Login */}
                    <div className="flex items-center gap-4">
                        <Link 
                            to="/member/login" 
                            className="text-sm text-white hover:text-[#C9A227] font-medium transition-colors"
                            data-testid="member-login-link"
                        >
                            Member Login
                        </Link>
                    </div>
                </div>

                {/* Mobile Navigation - Simple version */}
                <nav className="md:hidden flex items-center gap-4 mt-4 pt-4 border-t border-[#5A6350] overflow-x-auto">
                    {navItems.map((item) => (
                        item.children ? (
                            <div key={item.label} className="relative group">
                                <button className="text-sm text-white whitespace-nowrap flex items-center gap-1">
                                    {item.label}
                                    <ChevronDown className="w-3 h-3" />
                                </button>
                            </div>
                        ) : (
                            <Link
                                key={item.label}
                                to={item.href}
                                className="text-sm text-white hover:text-[#C9A227] whitespace-nowrap"
                            >
                                {item.label}
                            </Link>
                        )
                    ))}
                </nav>
            </div>
        </header>
    );
};

export default MainNav;
